import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Callout } from '../../components/workbook/Callout';
import { getPhaseColor, getPhaseDescription } from '../../lib/workbook/utils';

const prioritizePages = [
  { number: 1, title: 'Criteria Gate', description: 'Pass/fail before planning', duration: '10 min' },
  { number: 2, title: 'Capacity & WIP Limits', description: 'Reality check', duration: '10 min' },
  { number: 3, title: 'Sequence & Slice', description: 'Meaningful done', duration: '15 min' },
  { number: 4, title: 'Milestones & Signals', description: 'How we\'ll know', duration: '10 min' },
  { number: 5, title: 'Trade-offs & Anti-goals', description: 'Say no on purpose', duration: '10 min' },
  { number: 6, title: '90-day Plan', description: 'Zoom out, still light', duration: '15 min' },
  { number: 7, title: 'Play It Out', description: 'Commitment marketplace', duration: '10-15 min' },
  { number: 8, title: 'Sign-off & Retrieval', description: 'Make it stick', duration: '5 min' },
];

export default function PrioritizePhase() {
  const phaseColor = getPhaseColor('prioritize', 600);
  const phaseBg = getPhaseColor('prioritize', 50);

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: phaseBg, borderColor: phaseColor + '40' }}>
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold" style={{ backgroundColor: phaseColor }}>
              P
            </div>
            <div>
              <h1 className="text-3xl font-bold" style={{ color: phaseColor }}>Prioritize</h1>
              <p style={{ color: phaseColor + 'DD' }}>{getPhaseDescription('prioritize')}</p>
            </div>
          </div>
          <p className="max-w-3xl" style={{ color: phaseColor + 'CC' }}>
            Make final choices, sequence work, and commit to a plan. 
            Apply decision criteria, respect capacity limits, and publish 
            a clear roadmap with review cadence.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Phase Overview */}
            <div className="bg-[var(--surface)] p-6">
              <h2 className="text-xl font-semibold text-[var(--text-pri)] mb-4">
                Phase Overview
              </h2>
              <div className="space-y-4">
                <p className="text-[var(--text-sec)]">
                  Make final decisions and commit to action with capacity awareness. 
                  This phase helps you:
                </p>
                <ul className="space-y-2 text-[var(--text-sec)]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Apply decision criteria to the shortlist</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Make capacity-aware Now/Next/Later choices</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Sequence and slice for fast, meaningful outcomes</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: phaseColor }} />
                    <span>Publish a 90-day plan with anti-goals and review cadence</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Pages List */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-[var(--text-pri)]">
                Pages in This Phase
              </h2>
              {prioritizePages.map((page) => (
                <Link
                  key={page.number}
                  to={`/elevation/workbook/prioritize/page-${page.number}`}
                  className="block p-4 border border-[var(--rule)] hover:border-[var(--coral)] hover:bg-[var(--coral-dim)] transition-colors group"
                  style={{ borderColor: phaseColor + '40' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{ backgroundColor: phaseColor }}>
                        {page.number}
                      </div>
                      <div>
                        <h3 className="font-semibold text-[var(--text-pri)] group-hover:text-[var(--coral)]">
                          {page.title}
                        </h3>
                        <p className="text-sm text-[var(--text-sec)]">
                          {page.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
                      <Clock className="w-4 h-4" />
                      {page.duration}
                      <ArrowRight className="w-4 h-4 group-hover:text-[var(--coral)]" style={{ color: phaseColor }} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {/* Key Concepts */}
            <Callout type="why" phase="prioritize">
              <div className="space-y-2">
                <p className="font-semibold">Key Concepts:</p>
                <ul className="space-y-1 text-sm">
                  <li>• <strong>Criteria Gate:</strong> Pass/fail before planning</li>
                  <li>• <strong>Capacity:</strong> Respect WIP limits and reality</li>
                  <li>• <strong>Sequence:</strong> Order work for fast outcomes</li>
                  <li>• <strong>Commit:</strong> Publish plan with review cadence</li>
                </ul>
              </div>
            </Callout>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Start */}
            <div className="p-4 border" style={{ backgroundColor: phaseBg, borderColor: phaseColor + '40' }}>
              <h3 className="font-semibold mb-3" style={{ color: phaseColor + 'CC' }}>
                Quick Start
              </h3>
              <div className="space-y-2 text-sm" style={{ color: phaseColor + 'AA' }}>
                <p>1. Apply criteria gate</p>
                <p>2. Check capacity limits</p>
                <p>3. Sequence and slice</p>
                <p>4. Publish 90-day plan</p>
              </div>
            </div>

            {/* Timing Guide */}
            <div className="bg-[var(--surface)] border border-[var(--rule)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Timing Guide
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <div className="flex justify-between">
                  <span>Solo work:</span>
                  <span className="font-medium">~80 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Team session:</span>
                  <span className="font-medium">~120 min</span>
                </div>
                <div className="flex justify-between">
                  <span>Coach workshop:</span>
                  <span className="font-medium">~90 min</span>
                </div>
              </div>
            </div>

            {/* Success Criteria */}
            <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-4">
              <h3 className="font-semibold text-[var(--text-sec)] mb-3">
                Success Criteria
              </h3>
              <div className="space-y-2 text-sm text-[var(--text-sec)]">
                <p>✓ Criteria applied to shortlist</p>
                <p>✓ Capacity-aware choices made</p>
                <p>✓ Work sequenced and sliced</p>
                <p>✓ 90-day plan published</p>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between">
          <Link
            to="/elevation/workbook/home"
            className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
          >
            ← Back to Overview
          </Link>
          <Link
            to="/elevation/workbook/prioritize/page-1"
            className="inline-flex items-center gap-2 text-white px-6 py-3 font-semibold hover:opacity-90 transition-colors"
            style={{ backgroundColor: phaseColor }}
          >
            Start Prioritize Phase
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

